// Stage / priority / source / type display constants. Option LISTS come from
// the live field options at runtime — only presentation mappings live here.

// Stage migration (architecture.md PR#7): legacy single `Released` coexists
// with the two-gate `Released to QA` / `Released to Prod` until it drains.
// These predicates are the ONE place that lists the literals — when the org
// deletes `Released`, this file is the only edit.

/** Any "the code has shipped somewhere" stage, across both models. */
const RELEASED_STAGES = ['Released', 'Released to QA', 'Released to Prod']
export function isReleasedStage(stage: string | null): boolean {
  return stage !== null && RELEASED_STAGES.includes(stage)
}

/** Live in production: two-gate Prod, or legacy Released (which meant prod). */
const LIVE_STAGES = ['Released', 'Released to Prod']
export function isLiveStage(stage: string | null): boolean {
  return stage !== null && LIVE_STAGES.includes(stage)
}

/** Two-gate QA. The gate exists once the live Stage options offer it; under
 *  legacy single-gate options there is only the live gate. */
const QA_STAGE = 'Released to QA'
export function isQaStage(stage: string | null): boolean {
  return stage === QA_STAGE
}
export function hasQaGate(stageOptions: string[]): boolean {
  return stageOptions.includes(QA_STAGE)
}

/** Stages exempt from "days overdue" / the stalled-flame signal. */
export function isShippedOrParked(stage: string | null): boolean {
  return stage === 'Done' || isReleasedStage(stage)
}

/** Stages that stage-sync derives from observable signals — `Merged` (a linked
 *  PR merged) and the Released gates (the `release-shipped` event) — plus
 *  terminal `Done`. The "Move to" context menu omits these: a hand-set value
 *  would just be recomputed (or is terminal), so offering it is a footgun. The
 *  boards still list them as columns — there the stage list also groups work, so
 *  released/merged issues must stay visible. */
const AUTOMATION_DRIVEN_STAGES = ['Merged', 'Done', ...RELEASED_STAGES]
export function isManuallySettableStage(stage: string): boolean {
  return !AUTOMATION_DRIVEN_STAGES.includes(stage)
}

/** Keep a preferred column order, dropping stages the live field lacks. */
export function availableStages(preferred: string[], liveOptions: string[] | undefined): string[] {
  if (!liveOptions || liveOptions.length === 0) return preferred.filter((s) => s !== 'Released to QA' && s !== 'Released to Prod')
  return preferred.filter((s) => liveOptions.includes(s))
}

/** CSS variable (in globals.css) for each stage's dot/accent colour. */
export const STAGE_VAR: Record<string, string> = {
  Triage: '--st-triage',
  Backlog: '--st-backlog',
  Ready: '--st-ready',
  'In progress': '--st-progress',
  'In review': '--st-review',
  Merged: '--st-merged',
  Released: '--st-released',
  'Released to QA': '--st-released',
  'Released to Prod': '--st-done', // Prod = done-colour (intentional)
  Blocked: '--st-blocked',
  Waiting: '--st-waiting',
  Done: '--st-done',
}

/** lucide icon name per stage (resolved to components in the UI layer). */
export const STAGE_ICON: Record<string, string> = {
  Triage: 'inbox',
  Backlog: 'circle-dashed',
  Ready: 'circle',
  'In progress': 'circle-dot',
  'In review': 'eye',
  Merged: 'git-merge',
  Released: 'package-check',
  'Released to QA': 'flask-conical',
  'Released to Prod': 'package-check',
  Blocked: 'octagon-alert',
  Waiting: 'clock',
  Done: 'circle-check',
}

/**
 * Priority colour by severity. Live options are P1 (drop everything),
 * P2 (this sprint), P3 (scheduled), Backlog (no commitment — neutral).
 * The --p0..--p2 ramp is red → amber → orange; Backlog stays grey.
 */
export const PRIORITY_VAR: Record<string, string> = {
  P1: '--p0',
  P2: '--p1',
  P3: '--p2',
  Backlog: '--p-none',
}

export function priorityVar(priority: string | null): string {
  return (priority && PRIORITY_VAR[priority]) || '--p-none'
}

export const SOURCE_ICON: Record<string, string> = {
  us: 'users',
  helpdesk: 'life-buoy',
  'sentry-bot': 'bot',
  'datafix-bot': 'database-zap',
  'migration-bot': 'database-backup',
  'other-bot': 'bot-message-square',
}

/** Native issue type → card glyph (lucide name + colour var). */
export const TYPE_GLYPH: Record<string, { icon: string; varName: string }> = {
  Bug: { icon: 'bug', varName: '--p0' },
  Feature: { icon: 'sparkle', varName: '--st-review' },
  Maintenance: { icon: 'square-check', varName: '--st-ready' },
  Request: { icon: 'life-buoy', varName: '--st-released' },
}

// Teams/areas come live from the repo's area/* labels (hooks/use-teams.ts);
// only the display-name overrides live here (capitalisation mangles acronyms).
const AREA_LABEL: Record<string, string> = {
  devops: 'DevOps',
  qa: 'QA',
}

export function areaLabel(area: string): string {
  return AREA_LABEL[area] ?? (area ? area[0]!.toUpperCase() + area.slice(1) : area)
}

/** Health view precedence: an issue lands in exactly one system group. */
export const SYSTEM_LABEL_PRECEDENCE = [
  'system/regression-at-prod',
  'system/escalated',
  'system/stalled',
  'system/aging',
  'system/awaiting-verification-prod',
  'system/awaiting-verification-qa',
] as const

export function primarySystemLabel(labels: string[]): string | null {
  for (const label of SYSTEM_LABEL_PRECEDENCE) {
    if (labels.includes(label)) return label
  }
  return null
}

/** A `system/*` label that belongs in Health but isn't chained in the
 *  precedence list above. GitHub is editable out-of-band, so an issue can carry
 *  a `system/*` label the app doesn't recognise (a new automation label, a stray
 *  hand-add) — Health surfaces it under its raw name rather than dropping the
 *  issue. Rollout labels are excluded: they have their own `/rollouts` view and
 *  aren't a Health concern. */
export function otherSystemLabel(labels: string[]): string | null {
  const known = SYSTEM_LABEL_PRECEDENCE as readonly string[]
  const rollout = ROLLOUT_LABELS as readonly string[]
  return (
    labels.find((l) => l.startsWith('system/') && !known.includes(l) && !rollout.includes(l)) ??
    null
  )
}

/** Rollouts: changes that ship code which executes *after* deploy and must run
 * to completion across every production subscription before we trust them —
 * today system/datafix, soon system/migration. Labels are bot-applied
 * (n3oltd/work); add future post-deploy execution labels here. */
export const ROLLOUT_LABELS = ['system/datafix', 'system/migration'] as const

export function isRollout(labels: string[]): boolean {
  return labels.some((l) => (ROLLOUT_LABELS as readonly string[]).includes(l))
}

/** Short display name for the rollout kind a label set carries
 * ('datafix' | 'migration' | …), or null if none. Stays in sync with
 * ROLLOUT_LABELS — the prefix is stripped for the badge. */
export function rolloutKind(labels: string[]): string | null {
  const hit = ROLLOUT_LABELS.find((l) => labels.includes(l))
  return hit ? hit.replace('system/', '') : null
}

/** Synthetic Health group for issues every slice misses: in-flight work
 * that escaped triage hygiene (no area label / type / priority). */
export const MISSING_METADATA = 'missing metadata'

export function metadataGaps(issue: {
  labels: string[]
  typeName: string | null
  priority: string | null
}): string[] {
  const gaps: string[] = []
  if (!issue.labels.some((l) => l.startsWith('area/'))) gaps.push('no area label')
  if (!issue.typeName) gaps.push('no type')
  if (!issue.priority) gaps.push('no priority')
  return gaps
}

/** The single Health group an issue belongs to. Precedence: a known system
 *  label wins over an unrecognised one, which wins over a metadata gap — so a
 *  multi-signal issue lands in exactly one group. Returns 'none' for an issue
 *  Health wouldn't surface (no system label, no gaps); callers filter to the
 *  Health universe first, so that value never reaches the UI. */
export function healthCategory(issue: {
  labels: string[]
  typeName: string | null
  priority: string | null
}): string {
  return (
    primarySystemLabel(issue.labels) ??
    otherSystemLabel(issue.labels) ??
    (metadataGaps(issue).length > 0 ? MISSING_METADATA : 'none')
  )
}

/** Severity order for sorting — most urgent first. */
export const PRIORITY_ORDER = ['P1', 'P2', 'P3', 'Backlog'] as const

export function priorityIndex(priority: string | null): number {
  const i = priority ? (PRIORITY_ORDER as readonly string[]).indexOf(priority) : -1
  return i === -1 ? PRIORITY_ORDER.length : i
}
